import { useState, useRef, useEffect, useCallback } from "react";
import api from "../services/api";
import { enqueueProgress, enqueueComplete } from "../services/workoutOutbox";
import { useWakeLock } from "./useWakeLock";

/**
 * useWorkoutSession
 *
 * Encapsula todo o estado e lógica de sessão de treino ativa:
 * timer, setsLog, completions, rest, modal flags, etc.
 *
 * Nota: a inicialização de setsLog (que depende de assignedWorkouts e
 * selectedWorkoutAssignmentId) fica em Dashboard.js como shim, para
 * evitar dependência circular com useDashboardData.
 *
 * @param {object}   opts
 * @param {function} opts.setToast - callback({ type, message })
 */
export function useWorkoutSession({ setToast }) {
  const [workoutSession, setWorkoutSession]           = useState(null);
  const [activeRest, setActiveRest]                   = useState(null);
  const [workoutSummary, setWorkoutSummary]           = useState(null);
  const [previousSets, setPreviousSets]               = useState({});
  const [elapsedSeconds, setElapsedSeconds]           = useState(0);
  const [workoutModalPreStart, setWorkoutModalPreStart] = useState(false);
  const [workoutModalReadonly, setWorkoutModalReadonly] = useState(false);
  const [pendingViewAssignmentId, setPendingViewAssignmentId] = useState(null);

  // Refs internos — nunca precisam ser exportados
  const timerRef                  = useRef(null);
  const workoutSessionRef         = useRef(null);
  const savePartialProgressTimers = useRef({});
  const completingItemsRef        = useRef(new Set());
  const hiddenIntervalsRef        = useRef([]);
  const hiddenSinceRef            = useRef(null);

  // Mantém a ref sincronizada para closures assíncronas (completeExercise, updateSetLog)
  useEffect(() => {
    workoutSessionRef.current = workoutSession;
  }, [workoutSession]);

  // Mantém a tela acesa enquanto houver treino em andamento — independe do
  // modal estar aberto ou fechado, e sobrevive a fechar/reabrir a janela.
  useWakeLock(!!workoutSession);

  // Rastreia intervalos de ausência do browser para evitar distorção no cálculo de duração
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current) {
        hiddenIntervalsRef.current.push({ start: hiddenSinceRef.current, end: Date.now() });
        hiddenSinceRef.current = null;
        if (hiddenIntervalsRef.current.length > 200) {
          hiddenIntervalsRef.current = hiddenIntervalsRef.current.slice(-200);
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Timer de sessão
  useEffect(() => {
    if (!workoutSession) return;
    setElapsedSeconds(Math.floor((Date.now() - workoutSession.startedAt) / 1000));
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - workoutSession.startedAt) / 1000));
    }, 1000);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [workoutSession]);

  // Carrega sugestões de carga do ciclo anterior quando uma sessão fica ativa
  useEffect(() => {
    const planId = workoutSession?.assignmentId;
    if (!planId) { setPreviousSets({}); return; }
    api.get(`/workout-sessions/previous-sets/${planId}`)
      .then(r => setPreviousSets(r.data || {}))
      .catch(() => {});
  }, [workoutSession?.assignmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parseia a resposta do servidor para o formato local de sessão ──────────────
  const parseActiveSession = useCallback((s, existing = null) => {
    const parsed = {
      id: s.id,
      assignmentId: s.user_workout_plan_id,
      startedAt: Date.parse(s.started_at),
      exerciseCompletions: {},
      exerciseDurations: {},
      setsLog: {},
    };
    (s.items || []).forEach((it) => {
      if (!it.user_workout_plan_item_id) return;
      const itemId = it.user_workout_plan_item_id;
      if (!it.partial && it.completed_at) {
        parsed.exerciseCompletions[itemId] = Date.parse(it.completed_at);
        if (it.duration_minutes) parsed.exerciseDurations[itemId] = it.duration_minutes;
      }
      if (it.sets_log) parsed.setsLog[itemId] = it.sets_log;
    });
    if (existing && existing.id === parsed.id) {
      const localLog = existing.setsLog || {};
      for (const [idStr, localSets] of Object.entries(localLog)) {
        const itemId = Number(idStr);
        const serverSets = parsed.setsLog[itemId];
        const localDone  = Array.isArray(localSets)  ? localSets.filter((s) => s.done).length  : 0;
        const serverDone = Array.isArray(serverSets) ? serverSets.filter((s) => s.done).length : 0;
        if (localDone >= serverDone) parsed.setsLog[itemId] = localSets;
      }
      Object.entries(existing.exerciseCompletions || {}).forEach(([k, v]) => {
        if (!parsed.exerciseCompletions[k]) parsed.exerciseCompletions[k] = v;
      });
      Object.entries(existing.exerciseDurations || {}).forEach(([k, v]) => {
        if (!parsed.exerciseDurations[k]) parsed.exerciseDurations[k] = v;
      });
    }
    return parsed;
  }, []);

  const clearWorkoutSession = useCallback(() => {
    const session = workoutSessionRef.current;
    if (!session) return;
    try { localStorage.removeItem(`workout_session_${session.assignmentId}`); } catch (_) {}
    setWorkoutSession(null);
    setElapsedSeconds(0);
    setActiveRest(null);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // Marca um exercício como concluído e sincroniza com o backend
  const completeExercise = useCallback((item, _itemsOrdered, finalSets = null) => {
    const session = workoutSessionRef.current;
    if (!session) return;
    const id = item.id;
    if (completingItemsRef.current.has(id)) return;
    const completions = { ...(session.exerciseCompletions || {}) };
    if (completions[id]) return;
    completingItemsRef.current.add(id);

    setWorkoutSession((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, exerciseCompletions: { ...(prev.exerciseCompletions || {}), [id]: Date.now() } };
      try { localStorage.setItem(`workout_session_${merged.assignmentId}`, JSON.stringify(merged)); } catch (_) {}
      return merged;
    });

    const updated = { ...session, exerciseCompletions: { ...(session.exerciseCompletions || {}), [id]: Date.now() } };
    (async () => {
      if (updated.id) {
        try {
          const itemSetsLog = finalSets || (updated.setsLog || {})[item.id] || null;
          const res = await api.post(`/workout-sessions/${updated.id}/items`, {
            user_workout_plan_item_id: item.id,
            order_index: item.order_index || null,
            sets_log: itemSetsLog,
          });
          if (res.data?.duration_minutes) {
            setWorkoutSession((prev) => {
              if (!prev) return prev;
              const newDurations = { ...(prev.exerciseDurations || {}), [item.id]: res.data.duration_minutes };
              const next = { ...prev, exerciseDurations: newDurations };
              try { localStorage.setItem(`workout_session_${next.assignmentId}`, JSON.stringify(next)); } catch (_) {}
              return next;
            });
          }
        } catch (err) {
          // POST direto primeiro porque a resposta traz duration_minutes
          // (usado na UI). Falhou: em vez de só avisar e perder, entra na
          // mesma fila persistente do progresso parcial, que reenvia
          // sozinha quando a conexão voltar.
          console.error("Erro ao sincronizar conclusão:", err);
          enqueueComplete(updated.id, item.id, {
            user_workout_plan_item_id: item.id,
            order_index: item.order_index || null,
            sets_log: finalSets || (updated.setsLog || {})[item.id] || null,
          });
          setToast?.({ type: "info", message: "Sem conexão - o treino está salvo no aparelho e será enviado sozinho." });
        } finally {
          completingItemsRef.current.delete(id);
        }
      } else {
        completingItemsRef.current.delete(id);
      }
    })();
  }, [setToast]);

  const allExercisesCompleted = useCallback((assignment) => {
    if (!workoutSession || !assignment) return false;
    const items = (assignment.items || [])
      .slice()
      .sort((a, b) => Number(a.order_index || 0) - Number(b.order_index || 0));
    if (!items.length) return false;
    return items.every((it) => !!workoutSession.exerciseCompletions[it.id]);
  }, [workoutSession]);

  // Atualiza uma série específica e persiste parcialmente no servidor (debounce 700ms)
  const updateSetLog = useCallback((itemId, setIndex, patch) => {
    setWorkoutSession((prev) => {
      if (!prev) return prev;
      const prevSets = (prev.setsLog || {})[itemId] || [];
      const newSets  = prevSets.map((s, i) => (i === setIndex ? { ...s, ...patch } : s));
      const updated  = { ...prev, setsLog: { ...(prev.setsLog || {}), [itemId]: newSets } };
      try { localStorage.setItem(`workout_session_${updated.assignmentId}`, JSON.stringify(updated)); } catch (_) {}
      return updated;
    });
    if (savePartialProgressTimers.current[itemId]) {
      clearTimeout(savePartialProgressTimers.current[itemId]);
    }
    savePartialProgressTimers.current[itemId] = setTimeout(() => {
      const session = workoutSessionRef.current;
      if (!session?.id) return;
      const currentSets = (session.setsLog || {})[itemId];
      if (!currentSets) return;
      // Antes era um PATCH direto com .catch(console.error): se a rede
      // falhasse na academia, a série ficava só no estado local e o aluno
      // nunca era avisado (achado P0 da revisão mobile de 06/09/2026).
      // Agora vai pra fila persistente, que reenvia sozinha quando a
      // conexão voltar — ver services/workoutOutbox.js.
      const alreadyCompleted = !!(session.exerciseCompletions || {})[itemId];
      enqueueProgress(session.id, itemId, currentSets, alreadyCompleted);
    }, 700);
  }, []);

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return {
    workoutSession, setWorkoutSession,
    activeRest, setActiveRest,
    workoutSummary, setWorkoutSummary,
    previousSets,
    elapsedSeconds,
    workoutModalPreStart, setWorkoutModalPreStart,
    workoutModalReadonly, setWorkoutModalReadonly,
    pendingViewAssignmentId, setPendingViewAssignmentId,
    parseActiveSession,
    clearWorkoutSession,
    completeExercise,
    allExercisesCompleted,
    updateSetLog,
    formatElapsed,
  };
}
